/**
 * ThemeProbe — internal dev page that puts every styled primitive side by
 * side in both light and dark mode. The goal is regression-catching:
 * the next time we ship a glass / card / toggle / select / pill that
 * silently breaks in one mode, this page makes it obvious in one screenshot.
 *
 * Mounted at /dashboard/__theme-probe (system_admin only).
 */
import React, { useEffect, useState } from 'react';
import Logo, { LogoMark } from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { ShieldCheck, CheckCircle2, XCircle, Sparkles, MessageCircle, Lock, Unlock } from 'lucide-react';

function Probe({ title, dark, children }) {
  // Render two clones of the probe content — left side forced .light,
  // right side forced .dark — so both modes are visible without toggling.
  return (
    <div className="ve-card p-4">
      <div className="ve-eyebrow mb-2">{title}</div>
      <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-white/10">
        <div className={`p-4 ${dark ? 'dark bg-[#0A0A0A] text-white' : 'bg-[#F7F5F4] text-slate-900'}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

function PrimitiveRow() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button className="ve-btn-primary text-xs">Primary</button>
      <button className="ve-btn-secondary text-xs">Secondary</button>
      <button className="ve-btn-ghost text-xs">Ghost</button>
      <span className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30">
        Pass
      </span>
      <span className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30">
        Warn
      </span>
      <span className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30">
        Fail
      </span>
      <select className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs dark:border-white/15 dark:bg-white/[0.04]">
        <option>Option 1</option>
        <option>Option 2</option>
      </select>
      <input type="text" defaultValue="text input" className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs dark:border-white/15 dark:bg-white/[0.04]"/>
    </div>
  );
}

function GlassRow() {
  return (
    <div className="space-y-3">
      <div className="ve-glass rounded-lg p-3 flex items-center gap-3">
        <LogoMark className="h-5 w-5" noLink/>
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold">ve-glass header</span>
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle compact/>
          <LanguageSwitcher/>
        </div>
      </div>
      <div className="ve-card p-3 flex items-center gap-3">
        <ShieldCheck size={16} className="text-[#5E1F8B] dark:text-[#8B3FC5]"/>
        <span className="text-sm font-bold">ve-card</span>
        <span className="ml-auto text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7A5A00] dark:text-[#8B3FC5]">
          gold accent
        </span>
      </div>
    </div>
  );
}

function TonedTilesRow() {
  const tiles = [
    { Icon: CheckCircle2, label: 'Approve', tone: 'emerald' },
    { Icon: Sparkles, label: 'Improve', tone: 'amber' },
    { Icon: MessageCircle, label: 'Meeting', tone: 'amber' },
    { Icon: XCircle, label: 'Reject',  tone: 'rose' },
    { Icon: Lock, label: 'Locked', tone: 'rose' },
    { Icon: Unlock, label: 'Unlocked', tone: 'emerald' },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {tiles.map(({ Icon, label, tone }) => {
        const cls = {
          emerald: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
          amber:   'border-amber-500 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
          rose:    'border-rose-500 bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300',
        }[tone];
        return (
          <div key={label} className={`flex flex-col items-start gap-1 p-3 rounded-lg border-2 ${cls}`}>
            <Icon size={16}/>
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ThemeProbe() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    document.title = 'Theme probe · Vigilant Entities';
  }, []);
  return (
    <div className="ve-container py-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="ve-eyebrow">Internal · QA</div>
          <h1 className="display text-2xl font-bold mt-1">Theme contrast probe</h1>
          <p className="text-sm text-slate-600 dark:text-white/65 mt-1 max-w-2xl">
            Every styled primitive in both modes, side by side. If anything reads
            &ldquo;white on white&rdquo; or &ldquo;black on black&rdquo;, we catch it here in one shot.
          </p>
        </div>
        <button data-testid="theme-probe-refresh"
          onClick={() => setTick((t) => t + 1)} className="ve-btn-secondary text-xs">
          Re-render ({tick})
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="theme-probe">
        <Probe title="Light · Primitives" dark={false}><PrimitiveRow/></Probe>
        <Probe title="Dark · Primitives"  dark={true}><PrimitiveRow/></Probe>
        <Probe title="Light · Glass + Header" dark={false}><GlassRow/></Probe>
        <Probe title="Dark · Glass + Header"  dark={true}><GlassRow/></Probe>
        <Probe title="Light · Toned tiles"   dark={false}><TonedTilesRow/></Probe>
        <Probe title="Dark · Toned tiles"    dark={true}><TonedTilesRow/></Probe>
      </div>

      <p className="mt-6 text-[10.5px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/45">
        Tip: screenshot this page (system_admin only) and diff against the
        previous build whenever you ship a global CSS change.
      </p>
    </div>
  );
}
